import * as React from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Menu from 'material-ui-popup-state/HoverMenu'
import MenuItem from '@mui/material/MenuItem'
import { Link } from "@mui/material";
import PopupState, {
    bindHover,
    bindMenu
} from 'material-ui-popup-state'
import { MdOutlineArrowDropDown, MdOutlineArrowRight } from "react-icons/md";
import { styled } from "@mui/material/styles";
import NestedProductMarketingMenuContainer from './NestedProductMarketingMenuContainer';
import { MARKETING_MENU_CONFIG } from './marketingMenuConfig';
import { colors, radius, spacing } from "../RecipeDesigner/styles/designTokens";

export const MENU_SURFACE_SX = {
    borderRadius: radius.lg,
    border: `1px solid ${colors.border.default}`,
    boxShadow: "0 4px 16px rgba(15, 18, 30, 0.06)",
    overflow: "hidden",
};

const SubmenuItem = styled(MenuItem)({
    display: "grid",
    gridTemplateColumns: "22px 1fr",
    columnGap: spacing.md,
    alignItems: "start",
    whiteSpace: "normal",
    minHeight: "auto",
    margin: "1px 6px",
    padding: "8px 10px",
    borderRadius: radius.md,
    textDecoration: "none",
    transition: "background-color 120ms ease",
    "& svg": { color: colors.text.tertiary, transition: "color 120ms ease" },
    "&:hover, &.Mui-focusVisible": {
        backgroundColor: colors.accent.primarySubtle,
        "& svg": { color: colors.accent.primary },
    },
});

const ParentMenuLabel = ({ icon: Icon, label, sublabel, roles }) => (
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: "22px 1fr 16px",
            columnGap: spacing.md,
            alignItems: "start",
            width: "100%",
            minWidth: 0,
        }}
    >
        <Box component="span" sx={{ display: "flex", justifyContent: "center", mt: "2px" }}>
            <Icon size={18} strokeWidth={1.75} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
            <Box
                component="span"
                sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    lineHeight: 1.35,
                    letterSpacing: "-0.01em",
                    color: colors.text.primary,
                }}
            >
                {label}
            </Box>
            <Box
                component="span"
                sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mt: "2px",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    lineHeight: 1.45,
                    color: colors.text.tertiary,
                }}
            >
                {sublabel}
            </Box>
            {roles?.length > 0 && (
                <Box
                    component="span"
                    sx={{
                        display: "block",
                        mt: "4px",
                        fontSize: "0.6875rem",
                        fontWeight: 500,
                        letterSpacing: "0.04em",
                        color: colors.text.tertiary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {roles.join(" · ")}
                </Box>
            )}
        </Box>

        <Box component="span" sx={{ display: "flex", justifyContent: "flex-end", mt: "3px" }}>
            <MdOutlineArrowRight size={16} />
        </Box>
    </Box>
);

const ProductMarketingMenu = () => {
    return (
        <PopupState variant="popover" popupId="demoMenu">
            {popupState => (
                <React.Fragment>
                    <Button
                        {...bindHover(popupState)}
                        disableRipple
                        endIcon={
                            <MdOutlineArrowDropDown
                                size={18}
                                style={{
                                    transition: "transform 150ms ease",
                                    transform: popupState.isOpen ? "rotate(180deg)" : "none",
                                }}
                            />
                        }
                        sx={{
                            textTransform: "none",
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                            color: colors.text.primary,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: radius.md,
                            transition: "background-color 120ms ease",
                            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        }}
                    >
                        {MARKETING_MENU_CONFIG.triggerLabel}
                    </Button>
                    <Menu
                        {...bindMenu(popupState)}
                        disableScrollLock
                        transitionDuration={{ enter: 140, exit: 90 }}
                        marginThreshold={16}
                        slotProps={{
                            paper: {
                                elevation: 0,
                                sx: { ...MENU_SURFACE_SX, mt: "6px", width: 380 },
                            },
                        }}
                        MenuListProps={{ dense: true, sx: { py: "6px" } }}
                    >
                        {MARKETING_MENU_CONFIG.sections.map((section) => (
                            <NestedProductMarketingMenuContainer
                                key={section.href}
                                parentMenuOpen={true}
                                component={Link}
                                href={section.href}
                                label={
                                    <ParentMenuLabel
                                        icon={section.icon}
                                        label={section.label}
                                        sublabel={section.sublabel}
                                        roles={section.roles}
                                    />
                                }
                            >
                                {section.items.map((item) => (
                                    <SubmenuItem key={item.href} component={Link} href={item.href} disableGutters>
                                        <Box component="span" sx={{ display: "flex", justifyContent: "center", mt: "1px" }}>
                                            <item.icon size={17} {...(item.iconProps || { strokeWidth: 1.75 })} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "block",
                                                    fontSize: "0.8125rem",
                                                    fontWeight: 600,
                                                    lineHeight: 1.35,
                                                    letterSpacing: "-0.01em",
                                                    color: colors.text.primary,
                                                }}
                                            >
                                                {item.label}
                                            </Box>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    mt: "2px",
                                                    fontSize: "0.75rem",
                                                    lineHeight: 1.4,
                                                    color: colors.text.tertiary,
                                                }}
                                            >
                                                {item.sublabel}
                                            </Box>
                                        </Box>
                                    </SubmenuItem>
                                ))}
                            </NestedProductMarketingMenuContainer>
                        ))}
                    </Menu>
                </React.Fragment>
            )}
        </PopupState>
    )
}

export default ProductMarketingMenu
